import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

export interface DropdownOption {
  id: string | number | null;
  name: string;
  description?: string;
  [key: string]: any;
}

export interface CustomDropdownProps {
  value: string | number | null;
  options: DropdownOption[];
  onChange: (option: DropdownOption) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  buttonClassName?: string;
  dropdownClassName?: string;
  containerClassName?: string;
  containerStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  dropdownStyle?: React.CSSProperties;
  caretType?: "chevron" | "caret";
  showDefaultOption?: boolean;
  defaultOptionLabel?: string;
  isLoadingOptions?: boolean;
  noDataText?: string;
  align?: "left" | "right" | "top" | "bottom";
  direction?: "up" | "down";
  verticalAlign?: "top" | "bottom";
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  options,
  onChange,
  placeholder = "Select Option",
  disabled = false,
  loading = false,
  searchable = false,
  searchPlaceholder = "Search...",
  buttonClassName = "btn btn-outline-secondary d-flex align-items-center justify-content-between gap-2 px-3 py-2 text-dark bg-white border rounded-3 transition-all",
  dropdownClassName = "dropdown-menu show shadow-lg border rounded-3 p-1 animate-pop-in",
  containerClassName = "",
  containerStyle,
  buttonStyle,
  dropdownStyle,
  caretType = "chevron",
  showDefaultOption = false,
  defaultOptionLabel = "Default",
  isLoadingOptions = false,
  noDataText = "Tidak Ada Data",
  align = "left",
  direction,
  verticalAlign,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [coords, setCoords] = useState<{
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
    width: number;
    maxHeight: number;
    dropUp: boolean;
  }>({
    width: 0,
    maxHeight: 240,
    dropUp: false,
  });

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const desiredHeight = 240;

    const isForceTop = align === "top" || verticalAlign === "top" || direction === "up";
    const dropUp = isForceTop || (spaceBelow < 200 && spaceAbove > spaceBelow);
    const availableHeight = Math.max(
      100,
      dropUp ? Math.min(spaceAbove - 16, desiredHeight) : Math.min(spaceBelow - 16, desiredHeight)
    );

    const isRightAligned = align === "right";
    let left: number | undefined;
    let right: number | undefined;

    if (isRightAligned) {
      right = window.innerWidth - rect.right;
      if (right < 10) right = 10;
    } else {
      left = rect.left;
      if (left < 10) left = 10;
    }

    setCoords({
      top: dropUp ? undefined : rect.bottom + 4,
      bottom: dropUp ? window.innerHeight - rect.top + 4 : undefined,
      left,
      right,
      width: rect.width,
      maxHeight: availableHeight,
      dropUp,
    });
  }, [align, verticalAlign, direction]);

  useLayoutEffect(() => {
    if (isOpen) {
      updatePosition();
    } else {
      setSearchTerm("");
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (isOpen) {
      const handleScrollOrResize = () => {
        updatePosition();
      };
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);
      return () => {
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const filteredOptions = searchable && searchTerm.trim()
    ? options.filter(
        (opt) =>
          opt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (opt.description && opt.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : options;

  const selectedOption = options.find((opt) => opt.id === value);
  const isDefaultSelected = value === null || value === "" || value === undefined;

  const handleSelect = (option: DropdownOption) => {
    onChange(option);
    setIsOpen(false);
  };

  const isRight = align === "right";

  const computedDropdownStyle: React.CSSProperties = {
    position: "fixed",
    ...(coords.right !== undefined
      ? { right: `${coords.right}px` }
      : { left: `${coords.left}px` }),
    minWidth: `${coords.width}px`,
    maxWidth: "90vw",
    zIndex: 99999,
    maxHeight: `${coords.maxHeight}px`,
    overflowY: "auto",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    ...(coords.dropUp
      ? {
          bottom: `${coords.bottom}px`,
          top: "auto",
          transformOrigin: isRight ? "bottom right" : "bottom left",
        }
      : {
          top: `${coords.top}px`,
          bottom: "auto",
          transformOrigin: isRight ? "top right" : "top left",
        }),
    ...dropdownStyle,
  };

  return (
    <div
      className={`position-relative d-inline-block ${containerClassName}`}
      style={containerStyle}
      ref={dropdownRef}
    >
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled || loading}
        className={`${buttonClassName} ${(disabled || loading) ? "disabled opacity-50 pe-none" : ""}`}
        style={buttonStyle}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-truncate">
          {selectedOption ? selectedOption.name : (isDefaultSelected && showDefaultOption ? defaultOptionLabel : placeholder)}
        </span>
        {caretType === "chevron" ? (
          <i
            className={`fa-solid fa-chevron-down ms-2 ${isOpen ? "rotate-180" : ""}`}
            style={{ fontSize: "0.75rem", transition: "transform 0.2s ease" }}
          ></i>
        ) : (
          <i
            className={`fa-solid fa-caret-${isOpen ? "up" : "down"} ms-2 text-xs`}
            style={{ transition: "transform 0.2s ease" }}
          ></i>
        )}
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className={dropdownClassName}
            style={computedDropdownStyle}
          >
            {searchable && (
              <div className="p-1 border-bottom mb-1 bg-light rounded-top">
                <input
                  type="text"
                  className="form-control form-control-sm py-0 px-2"
                  style={{ fontSize: "0.75rem" }}
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
              </div>
            )}

            {isLoadingOptions ? (
              <div className="px-3 py-2 small text-muted">Loading...</div>
            ) : !showDefaultOption && filteredOptions.length === 0 ? (
              <div className="px-3 py-2 small text-muted">{noDataText}</div>
            ) : (
              <>
                {showDefaultOption && (
                  <button
                    type="button"
                    className={`dropdown-item rounded-2 py-2 px-3 text-nowrap d-flex align-items-center justify-content-between ${
                      isDefaultSelected ? "active bg-primary text-white fw-bold" : "text-dark"
                    }`}
                    onClick={() => handleSelect({ id: null, name: defaultOptionLabel })}
                  >
                    <span>{defaultOptionLabel}</span>
                    {isDefaultSelected && <i className="fa-solid fa-check ms-2 text-xs"></i>}
                  </button>
                )}
                {filteredOptions.map((opt, idx) => {
                  const isSelected = value === opt.id;
                  return (
                    <button
                      key={opt.id ?? idx}
                      type="button"
                      className={`dropdown-item rounded-2 p-2 ${
                        isSelected ? "active bg-primary text-white fw-bold" : "text-dark"
                      }`}
                      style={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        display: "block",
                        height: "auto",
                        minHeight: "fit-content",
                      }}
                      onClick={() => handleSelect(opt)}
                    >
                      <div className="d-flex align-items-start justify-content-between w-100">
                        <div className="d-flex flex-column text-start me-2" style={{ flex: 1, minWidth: 0, whiteSpace: "normal" }}>
                          <span style={{ fontWeight: 600, fontSize: "0.75rem", lineHeight: "1.25", whiteSpace: "normal" }}>
                            {opt.name}
                          </span>
                          {opt.description && (
                            <small
                              className={isSelected ? "text-white-50" : "text-muted"}
                              style={{ fontSize: "0.68rem", fontWeight: "normal", marginTop: "3px", lineHeight: "1.2", whiteSpace: "normal" }}
                            >
                              {opt.description}
                            </small>
                          )}
                        </div>
                        {isSelected && <i className="fa-solid fa-check ms-2 text-xs flex-shrink-0 mt-1"></i>}
                      </div>
                    </button>
                  );
                })}
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default CustomDropdown;


